import { makeAutoObservable, runInAction } from "mobx";
import { toast } from "react-toastify";
import { NEXT_BTN, PREVIOUS_BTN } from "../../../constants/constants";
import {
  COMPANYPROFILEINITIAL,
  EIDR_INITIAL,
  GHG_REPORTING_INITIAL,
  HQ_INITIAL,
  IPB_INITIAL,
  SBTI_INITIAL,
  SUBSIDIARY_INITIAL,
} from "../constants/onboard.constant";
import {
  _handleGetYearOptions,
  calculateTargetReduction,
} from "../utils/onboard.util";
import {
  childCompaniesURL,
  companyURL,
  multiCompanyURL,
  myCompanyURL,
} from "../../User/api/url";
import { benchmarkURL, EIDRURL, ghgReportingURL, sbtiURL } from "../api/url";
import { HQ, SUBSIDIARY } from "../../../constants/commonText.constants";
import { handleFetch } from "../../../utils/handleFetch";

class OnboardStore {
  selectedPage = 0;
  companyProfile = COMPANYPROFILEINITIAL;
  initialYear = 2024;
  currentYear = new Date().getFullYear();
  baseYearOptions = [];
  targetYearOptions = [];
  GHGReportingForm = GHG_REPORTING_INITIAL;
  IPBData = [];
  IPBDataForm = IPB_INITIAL;
  SBTiSettingForm = SBTI_INITIAL;
  EIDRData = EIDR_INITIAL;
  childCompanies = [HQ_INITIAL, SUBSIDIARY_INITIAL];
  deletedCompanies = [];
  updatedCompanies = [];
  newHQList = [];
  newSubsidiaryList = [];
  parentHQName = null;
  parentHQId = null;
  updatedChildCompanyList = [];
  HQSelfUpdate = false;
  subsidiarySelfUpdate = false;
  updateSubsidiaryName = "";
  isBoundaryChanged = false;
  errors = {};
  isDeleteConfirmModalOpen = false;
  selectedDeleteCompanyId = null;
  selectedCompanyName = null;
  isCompanyProfileChanged = false;
  isGHGReportingChanged = false;
  isEIDRChanged = false;
  isIPBChanged = false;
  isSBTiChanged = false;
  isGeneralConfirmationModalOpen = false;

  constructor() {
    makeAutoObservable(this);
  }

  openGeneralConfirmationModal = () =>
    (this.isGeneralConfirmationModalOpen = true);

  closeGeneralConfirmationModal = () =>
    (this.isGeneralConfirmationModalOpen = false);

  handleSaveSummary = async () => {
    if (this.isCompanyProfileChanged) {
      await this.updateCompany();
    }

    if (this.isGHGReportingChanged) {
      this.updateGHGReporting();
    }

    if (this.isBoundaryChanged) {
      await this.saveBoundary();
    }

    if (this.isEIDRChanged) {
      await this.saveEIDR();
    }

    if (this.isIPBChanged) {
      await this.saveIPBData();
    }

    if (this.isSBTiChanged) {
      await this.saveSBTI();
    }

    this.isGeneralConfirmationModalOpen = false;
    return true;
  };

  setErrors = (v) => (this.errors = v);

  openDeleteConfirmModal = (companyId, companyName) => {
    this.selectedDeleteCompanyId = companyId;
    this.selectedCompanyName = companyName;
    this.isDeleteConfirmModalOpen = true;
  };

  closeDeleteConfirmModal = () => {
    this.isDeleteConfirmModalOpen = false;
  };

  getChildCompanies = async () => {
    return await handleFetch({
      url: childCompaniesURL,
      method: "GET",
      onSuccess: (data) => {
        runInAction(() => {
          if (data?.length === 0) {
            this.childCompanies = [HQ_INITIAL, SUBSIDIARY_INITIAL];
          } else {
            this.childCompanies = data;
          }
        });
      },
    });
  };

  saveBoundary = async () => {
    return await handleFetch({
      url: ghgReportingURL,
      method: "PUT",
      body: this.GHGReportingForm,
      onSuccess: (data) => {
        runInAction(async () => {
          this.GHGReportingForm = data;
          // this.updatedChildCompanyList = [...this.childCompanies, ...this.newHQList, ...this.newSubsidiaryList]
          this.updatedChildCompanyList = [
            ...this.newHQList,
            ...this.newSubsidiaryList,
          ];
          const newOnes = this.updatedChildCompanyList.filter(
            (company) => !company._id && company.companyName !== "",
          );
          const updateOnes = this.updatedChildCompanyList.filter((company) =>
            this.updatedCompanies.includes(company._id),
          );
          if (
            JSON.stringify(this.updatedChildCompanyList) !==
            JSON.stringify([HQ_INITIAL, SUBSIDIARY_INITIAL])
          ) {
            const successCreate =
              newOnes.length > 0 ? await this.createMultiCompany(newOnes) : [];
            const successUpdate =
              updateOnes.length > 0
                ? await this.updateMultiCompany(updateOnes)
                : [];
            const successDelete = await this.deleteMultiCompany();

            if (successCreate && successUpdate && successDelete) {
              toast.success("Successfully saved");
              this.isBoundaryChanged = false;
            }
          }
        });
      },
    });
  };

  createMultiCompany = async (newCompanies) => {
    return await handleFetch({
      url: multiCompanyURL,
      method: "POST",
      body: newCompanies,
      onSuccess: () => {
        runInAction(() => {
          this.getChildCompanies();
          this.newHQList = [];
          this.newSubsidiaryList = [];
          this.subsidiarySelfUpdate = false;
        });
      },
      onValidationError: () => toast.error("Please fill company information"),
    });
  };

  updateMultiCompany = async (companies) => {
    return await handleFetch({
      url: multiCompanyURL,
      method: "PUT",
      body: companies,
      onSuccess: () => {
        runInAction(() => {
          this.getChildCompanies();
          // this.newHQList = [];
          // this.newSubsidiaryList = [];
          this.subsidiarySelfUpdate = false;
        });
      },
    });
  };

  deleteMultiCompany = async () => {
    return await handleFetch({
      url: multiCompanyURL,
      method: "DELETE",
      body: this.deletedCompanies,
      onSuccess: () => {
        runInAction(() => {
          this.getChildCompanies();
          // this.newHQList = [];
          // this.newSubsidiaryList = [];
        });
      },
    });
  };

  getEIDR = async () => {
    return await handleFetch({
      url: EIDRURL,
      method: "GET",
      onSuccess: (data) =>
        runInAction(() => (this.EIDRData = { ...EIDR_INITIAL, ...data })),
    });
  };

  saveEIDR = async () => {
    return await handleFetch({
      url: EIDRURL,
      method: "PUT",
      body: this.EIDRData,
      onSuccess: (data, message) => {
        runInAction(() => {
          this.EIDRData = data;
          this.isEIDRChanged = false;
        });
        toast.success(message);
      },
    });
  };

  handleChangeEIDR = (key, type, value) => {
    this.EIDRData[key][type] = value;
    this.isEIDRChanged = true;
  };

  getSBTI = async () => {
    return await handleFetch({
      url: sbtiURL,
      method: "GET",
      onSuccess: (data) => runInAction(() => (this.SBTiSettingForm = data)),
    });
  };

  saveSBTI = async () => {
    return await handleFetch({
      url: sbtiURL,
      method: "PUT",
      body: this.SBTiSettingForm,
      onSuccess: (data, message) => {
        runInAction(() => {
          this.SBTiSettingForm = data;
          this.isSBTiChanged = false;
        });
        toast.success(message);
      },
    });
  };

  getIPB = async () => {
    return await handleFetch({
      url: benchmarkURL(),
      method: "GET",
      onSuccess: (data) => runInAction(() => (this.IPBData = data)),
    });
  };

  saveIPBData = async () => {
    const { _id, ...body } = this.IPBDataForm;
    const url = _id ? benchmarkURL(_id) : benchmarkURL();

    return await handleFetch({
      url,
      method: _id ? "PUT" : "POST",
      body,
      onSuccess: (data, message) => {
        runInAction(() => {
          const index = this.IPBData.findIndex((d) => d._id === data._id);
          if (index > -1) {
            runInAction(() => (this.IPBData[index] = data));
          } else {
            runInAction(() => this.IPBData.push(data));
          }
          this.errors = {};
          this.isIPBChanged = false;
        });
        toast.success(message);
      },
      onValidationError: (errors) => runInAction(() => (this.errors = errors)),
    });
  };

  handleSelectIPB = (id) => {
    const ipb = this.IPBData.find((d) => d._id === id) || IPB_INITIAL;
    this.IPBDataForm = { ...ipb };
  };

  deleteIPBData = async (id) => {
    return await handleFetch({
      url: benchmarkURL(id),
      method: "DELETE",
      onSuccess: (data, message) => {
        runInAction(() => {
          const index = this.IPBData.findIndex((d) => d._id === data._id);
          if (index > -1) {
            this.IPBData.splice(index, 1);
          }
          this.isDeleteConfirmModalOpen = false;
        });
        toast.success(message);
      },
    });
  };

  getCompany = async () => {
    return await handleFetch({
      url: myCompanyURL,
      method: "GET",
      onSuccess: (data) => runInAction(() => (this.companyProfile = data)),
    });
  };

  updateCompany = async () => {
    return await handleFetch({
      url: companyURL(this.companyProfile?._id),
      method: "PUT",
      body: this.companyProfile,
      onSuccess: (data, message) => {
        runInAction(() => {
          this.companyProfile = data;
          this.isCompanyProfileChanged = false;
        });
        toast.success(message);
      },
    });
  };

  getGHGReporting = async () => {
    return await handleFetch({
      url: ghgReportingURL,
      method: "GET",
      onSuccess: (data) => runInAction(() => (this.GHGReportingForm = data)),
    });
  };

  updateGHGReporting = async () => {
    return await handleFetch({
      url: ghgReportingURL,
      method: "PUT",
      body: this.GHGReportingForm,
      onSuccess: (data, message) => {
        runInAction(() => {
          this.GHGReportingForm = data;
          this.isGHGReportingChanged = false;
        });
        toast.success(message);
      },
    });
  };

  handleChangeCompanyProfile = (e) => {
    const { id, value } = e.target;
    this.companyProfile[id] = value;
    this.isCompanyProfileChanged = true;
  };

  handlePageChange = (type, page, companyId) => {
    if (type === NEXT_BTN.key) {
      this.selectedPage++;
    } else if (type === PREVIOUS_BTN.key) {
      this.selectedPage--;
    } else {
      this.selectedPage = Number(page);
    }
    window.history.replaceState(
      null,
      "",
      `/onboarding/${this.selectedPage}${companyId ? `/${companyId}` : ""}`,
    );
  };

  handleGetBaseYearOptions = () => {
    this.baseYearOptions = _handleGetYearOptions(2020, this.currentYear);
  };

  handleGetTargetYearOptions = () => {
    this.targetYearOptions = _handleGetYearOptions(this.currentYear + 1, 2050);
  };

  handleChangeGHGReporting = (e) => {
    this.isBoundaryChanged = true;
    const { id, value } = e.target;
    this.GHGReportingForm[id] = value;
    this.isGHGReportingChanged = true;
  };

  handleChangeIPB = (e) => {
    const { id, value } = e.target;
    this.IPBDataForm[id] = value;
    this.isIPBChanged = true;
  };

  handleChangeSBTiSetting = (e) => {
    const { id, value } = e.target;
    this.SBTiSettingForm[id] = value;
    this.isSBTiChanged = true;
  };

  handleCheckReductionScope = (e) => {
    this.isSBTiChanged = true;
    const { id, value } = e.target;
    const [key1, key2] = id.split("_");

    if (!this.SBTiSettingForm.reductionScopes) {
      this.SBTiSettingForm.reductionScopes = {};
    }
    if (!this.SBTiSettingForm.reductionScopes[key1]) {
      this.SBTiSettingForm.reductionScopes[key1] = {};
    }

    this.SBTiSettingForm.reductionScopes[key1][key2] = value;
  };

  addHQ = () => {
    this.isBoundaryChanged = true;
    this.newSubsidiaryList = [];
    this.newHQList.push(HQ_INITIAL);
  };

  addSubsidiary = (hqName, hqID) => {
    this.isBoundaryChanged = true;
    if (!this.HQSelfUpdate) {
      this.newHQList = [];
    }
    SUBSIDIARY_INITIAL.HQId = hqID;
    if (
      this.newSubsidiaryList.length > 0 &&
      this.parentHQName === hqName &&
      this.parentHQId === hqID
    ) {
      this.newSubsidiaryList.push(SUBSIDIARY_INITIAL);
    } else {
      this.newSubsidiaryList = [];
      this.parentHQName = hqName;
      this.parentHQId = hqID;
      this.newSubsidiaryList.push(SUBSIDIARY_INITIAL);
    }
  };

  removeSubsidiary = (index, id) => {
    this.isBoundaryChanged = true;
    if (id && !this.deletedCompanies.includes(id)) {
      this.deletedCompanies.push(id);
    }
    this.newSubsidiaryList.splice(index, 1);
  };

  removeHQ = (index, id) => {
    this.isBoundaryChanged = true;
    if (id && !this.deletedCompanies.includes(id)) {
      this.deletedCompanies.push(id);
      const dList = this.childCompanies?.filter((c) => c._id == id)[0]
        ?.subsidiaries;
      if (dList.length > 0) {
        dList.map((sub) => this.deletedCompanies.push(sub._id));
      }
    }

    this.newHQList.splice(index, 1);
  };

  handleChangeSubsidiary = (e, index, _id) => {
    this.isBoundaryChanged = true;
    if (_id && !this.updatedCompanies.includes(_id)) {
      this.updatedCompanies.push(_id);
    }
    const { id, value } = e.target;
    this.newSubsidiaryList[index][id] = value;
  };

  handleChangeHQ = (e, index, _id) => {
    this.isBoundaryChanged = true;
    if (_id && !this.updatedCompanies.includes(_id)) {
      this.updatedCompanies.push(_id);
    }
    const { id, value } = e.target;
    this.newHQList[index][id] = value;
  };

  editAllHQ = () => {
    this.HQSelfUpdate = false;
    this.newSubsidiaryList = [];
    const tempCompanies = this.childCompanies
      ?.filter((c) => c.cLevel === HQ)
      .map(({ subsidiaries, ...rest }) => rest);
    this.newHQList = tempCompanies;
  };

  editHQ = (hqName, hqID) => {
    this.HQSelfUpdate = true;
    this.parentHQName = hqName;
    this.parentHQId = hqID;
    const tempCompany = this.childCompanies
      ?.filter((c) => c.cLevel === HQ && c._id === hqID)
      .map((c) => ({ ...c }));
    this.newHQList = tempCompany;
    this.newSubsidiaryList =
      tempCompany[0].subsidiaries?.map((c) => ({ ...c })) || [];
  };

  handleCancel = () => {
    this.HQSelfUpdate = false;
    this.subsidiarySelfUpdate = false;
    this.newHQList = [];
    this.newSubsidiaryList = [];
    this.updateSubsidiaryName = "";
    this.parentHQName = null;
    this.parentHQId = null;
    this.isBoundaryChanged = false;
  };

  editAllSubsidiary = () => {
    this.subsidiarySelfUpdate = false;
    this.newHQList = [];
    this.newSubsidiaryList = [];
    const tempCompanies = this.childCompanies
      ?.filter((c) => c.cLevel === SUBSIDIARY)
      .map((com) => ({ ...com }));
    this.newSubsidiaryList = tempCompanies;
  };

  editSubsidiary = (parentHQID, subsidiaryName, subsidiaryID) => {
    this.newHQList = [];
    this.subsidiarySelfUpdate = true;
    this.updateSubsidiaryName = subsidiaryName;
    const tempCompany = this.childCompanies
      ?.filter((c) => c.cLevel === SUBSIDIARY && c._id === subsidiaryID)
      .map((c) => ({ ...c }));
    const subTempCompany = this.childCompanies
      ?.filter((subc) => subc._id === parentHQID)[0]
      ?.subsidiaries?.filter(
        (sub) => sub.cLevel === SUBSIDIARY && sub._id === subsidiaryID,
      )
      .map((subCom) => ({ ...subCom }));
    this.newSubsidiaryList = parentHQID === null ? tempCompany : subTempCompany;
  };

  get getTotalTargetReduction() {
    const { reductionScopes = {} } = this.SBTiSettingForm || {};
    const total = Object.entries(reductionScopes).reduce(
      (acc, [key, { isSelect, baseline }]) => {
        return isSelect ? acc + (Number(baseline) || 0) : acc;
      },
      0,
    );

    const totalTargetReduction =
      total *
      (Number(calculateTargetReduction(this.SBTiSettingForm)) / 100 || 1);

    return totalTargetReduction;
  }

  get totalReductionBaseline() {
    const { reductionScopes = {} } = this.SBTiSettingForm || {};
    return Object.entries(reductionScopes).reduce(
      (a, [key, { isSelect, baseline }]) => {
        if (key === "_id") return a;
        const value = isSelect ? parseFloat(baseline) || 0 : 0;
        return a + value;
      },
      0,
    );
  }
}

export const onboardStore = new OnboardStore();
